import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Download,
  FileDownload,
  Assessment,
  CalendarToday,
  Description,
  PictureAsPdf,
  TableChart,
  Refresh,
} from '@mui/icons-material';
import { reportsService } from '../services/api';

interface Report {
  id: string;
  name: string;
  type: string;
  generatedDate: string;
  size: string;
  status: 'ready' | 'processing' | 'failed';
}

const Reports: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reportType, setReportType] = useState('revenue');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
  });
  const [format, setFormat] = useState('pdf');
  const [recentReports, setRecentReports] = useState<Report[]>([
    {
      id: '1',
      name: 'Monthly Revenue Report',
      type: 'revenue',
      generatedDate: new Date().toISOString(),
      size: '2.4 MB',
      status: 'ready',
    },
    {
      id: '2',
      name: 'User Growth Analysis',
      type: 'users',
      generatedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      size: '1.8 MB',
      status: 'ready',
    },
    {
      id: '3',
      name: 'Agent Performance Report',
      type: 'agents',
      generatedDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      size: '3.1 MB',
      status: 'ready',
    },
  ]);

  const reportTypes = [
    { value: 'revenue', label: 'Revenue Report', icon: <Assessment /> },
    { value: 'users', label: 'User Analytics', icon: <Assessment /> },
    { value: 'benefits', label: 'Benefits Usage', icon: <Assessment /> },
    { value: 'agents', label: 'Agent Performance', icon: <Assessment /> },
    { value: 'redemptions', label: 'Redemptions Summary', icon: <Assessment /> },
    { value: 'giveaways', label: 'Giveaway Analytics', icon: <Assessment /> },
  ];

  const handleGenerateReport = async () => {
    try {
      setLoading(true);
      setError(null);

      const reportData = await reportsService.generateReport(reportType, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        format,
      });

      // Simulate report generation
      const newReport: Report = {
        id: Date.now().toString(),
        name: `${reportTypes.find(r => r.value === reportType)?.label} - ${new Date().toLocaleDateString()}`,
        type: reportType,
        generatedDate: new Date().toISOString(),
        size: '2.1 MB',
        status: 'ready',
      };

      setRecentReports([newReport, ...recentReports]);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async (report: Report) => {
    try {
      const blob = await reportsService.exportReport(report.type, format as 'csv' | 'pdf');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download report');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ready': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
          Reports
        </Typography>
        <Typography variant="body1" sx={{ color: '#888' }}>
          Generate and download comprehensive reports
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Report Generator */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
                Generate New Report
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>Report Type</InputLabel>
                  <Select
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                    label="Report Type"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    {reportTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Start Date"
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />

                <TextField
                  label="End Date"
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: 'white',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    },
                    '& .MuiInputLabel-root': { color: '#888' },
                  }}
                />

                <FormControl fullWidth>
                  <InputLabel sx={{ color: '#888' }}>Format</InputLabel>
                  <Select
                    value={format}
                    onChange={(e) => setFormat(e.target.value)}
                    label="Format"
                    sx={{
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                    }}
                  >
                    <MenuItem value="pdf">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PictureAsPdf sx={{ fontSize: 20 }} />
                        PDF
                      </Box>
                    </MenuItem>
                    <MenuItem value="csv">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TableChart sx={{ fontSize: 20 }} />
                        CSV
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleGenerateReport}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <FileDownload />}
                  sx={{
                    bgcolor: '#FFD700',
                    color: '#0A0A0A',
                    fontWeight: 'bold',
                    py: 1.5,
                    '&:hover': { bgcolor: '#E6C200' },
                    '&:disabled': {
                      bgcolor: 'rgba(255, 215, 0, 0.3)',
                      color: 'rgba(10, 10, 10, 0.5)',
                    },
                  }}
                >
                  {loading ? 'Generating...' : 'Generate Report'}
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Quick Reports */}
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)', mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 3 }}>
                Quick Reports
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<CalendarToday />}
                  sx={{
                    color: '#FFD700',
                    borderColor: '#FFD700',
                    '&:hover': {
                      borderColor: '#E6C200',
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  Today's Summary
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Assessment />}
                  sx={{
                    color: '#FFD700',
                    borderColor: '#FFD700',
                    '&:hover': {
                      borderColor: '#E6C200',
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  Weekly Overview
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<Description />}
                  sx={{
                    color: '#FFD700',
                    borderColor: '#FFD700',
                    '&:hover': {
                      borderColor: '#E6C200',
                      bgcolor: 'rgba(255, 215, 0, 0.1)',
                    },
                  }}
                >
                  Monthly Statement
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Reports */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ bgcolor: '#1A1A1A', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                  Recent Reports
                </Typography>
                <IconButton sx={{ color: '#FFD700' }}>
                  <Refresh />
                </IconButton>
              </Box>

              <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Report Name
                      </TableCell>
                      <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Type
                      </TableCell>
                      <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Generated
                      </TableCell>
                      <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Size
                      </TableCell>
                      <TableCell sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#888', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentReports.map((report) => (
                      <TableRow key={report.id}>
                        <TableCell sx={{ color: 'white', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Description sx={{ color: '#FFD700', fontSize: 20 }} />
                            {report.name}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          {reportTypes.find(t => t.value === report.type)?.label}
                        </TableCell>
                        <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          {new Date(report.generatedDate).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ color: '#CCC', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          {report.size}
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          <Chip
                            label={report.status}
                            size="small"
                            color={getStatusColor(report.status)}
                            sx={{ fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                          {report.status === 'ready' && (
                            <IconButton
                              size="small"
                              sx={{ color: '#4ECDC4' }}
                              onClick={() => handleDownloadReport(report)}
                            >
                              <Download />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;